import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./dialog";
import {
  ArrowRight,
  Check,
  FileText,
  PenLine,
  Hospital,
  Star,
  Crown
} from "lucide-react";
import { submitGeneralFeedback } from "../../apis/supportService";
import toast from "react-hot-toast";

const BENEFITS = [
  {
    title: "Unlimited notes",
    detail: "No monthly cap on Pro & Elite",
    icon: FileText,
  },
  {
    title: "Magic Editor & HPI",
    detail: "Refine notes and history in seconds",
    icon: PenLine,
  },
  // {
  //   title: "Optional EMR input",
  //   detail: "Human chart entry when you need it",
  //   icon: Hospital,
  // },
];

function CountdownRing({ days, warnDays = 3 }) {
  const value = Number.isFinite(days) ? Math.max(0, Math.min(warnDays, days)) : 0;
  const progress = warnDays <= 0 ? 0 : value / warnDays;
  const size = 76;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - progress);

  return (
    <div
      className="relative flex h-[76px] w-[76px] shrink-0 items-center justify-center"
      aria-hidden
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.22)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="white"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
        <span className="text-[1.75rem] font-bold leading-none tracking-tight tabular-nums">
          {days === 0 ? "0" : days}
        </span>
        <span className="mt-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-white/85">
          {days === 1 ? "day" : "days"}
        </span>
      </div>
    </div>
  );
}

/**
 * Login reminder when plan/trial is exhausted, or ≤3 days remaining.
 */
const ratingDescriptions = {
  1: { emoji: '😢', text: 'Very Dissatisfied', color: 'text-red-500 font-semibold' },
  2: { emoji: '🙁', text: 'Dissatisfied', color: 'text-orange-500 font-semibold' },
  3: { emoji: '😐', text: 'Neutral', color: 'text-yellow-600 font-semibold' },
  4: { emoji: '🙂', text: 'Satisfied', color: 'text-emerald-600 font-semibold' },
  5: { emoji: '😄', text: 'Very Satisfied', color: 'text-green-600 font-semibold' },
};

const categories = [
  { value: 'UI', label: 'Usability & Design' },
  { value: 'FEATURE', label: 'AI Note Accuracy' },
  { value: 'PERFORMANCE', label: 'Speed & Performance' },
  { value: 'SUPPORT', label: 'Customer Support' },
  { value: 'BILLING', label: 'Billing & Subscriptions' },
  { value: 'OTHER', label: 'General / Other' },
];

const PlanReminderModal = ({
  isOpen,
  onClose,
  onSubscribe,
  onViewPricing,
  reminder = null,
  isLoading = false,
  currentUser = null,
  refreshMe = null,
}) => {
  const [rating, setRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('OTHER');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedbackSubmit = async (andUpgrade = false) => {
    const hasRating = rating !== null && rating > 0;
    const hasMessage = message && message.trim() !== '';

    if (!hasRating && !hasMessage) {
      toast.error('Please select a star rating or share your feedback in the comments.');
      return;
    }

    setIsSubmitting(true);
    try {
      const firstName = currentUser?.profile?.firstName || '';
      const lastName = currentUser?.profile?.lastName || '';
      const email = currentUser?.email || '';
      const company = currentUser?.profile?.organization || currentUser?.profile?.practiceName || '';
      const role = currentUser?.profile?.roleTitle || '';
      const name = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (currentUser?.email?.split('@')[0] || '');

      await submitGeneralFeedback({
        rating: rating || null,
        category: selectedCategory,
        message: message || null,
        submittedName: name || null,
        submittedEmail: email || null,
        role: role || null,
        company: company || null,
      });

      toast.success('Thank you for rating us!');



      if (refreshMe) {
        await refreshMe();
      }

      if (andUpgrade) {
        onSubscribe?.();
      } else {
        onClose();
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || 'Could not submit your review. Please try again.';
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAndDismiss = () => {
    onClose();
  };
  const variant = reminder?.variant || "exhausted";
  const days = Number(reminder?.daysRemaining);
  const isExpiring = variant === "expiring_soon";
  const sourceLabel =
    reminder?.source === "subscription" ? "subscription" : "free trial";

  const dayCount = Number.isFinite(days) ? Math.max(0, Math.ceil(days)) : null;

  const title = isExpiring
    ? dayCount === 0
      ? "Today is your last day"
      : "Don't lose your momentum"
    : "Let's get you back on track";

  const description = isExpiring
    ? `Your ${sourceLabel} ends ${dayCount === 0
      ? "today"
      : dayCount === 1
        ? "in 1 day"
        : `in ${dayCount} days`
    }. Upgrade to keep charting without interruption.`
    : reminder?.message ||
    "Your free trial or subscription has ended. Choose a plan to continue creating SOAP notes.";

  const statusChip = isExpiring
    ? dayCount === 0
      ? "Ends today"
      : dayCount === 1
        ? "1 day left"
        : `${dayCount} days left`
    : "Access paused";

  const showFeedbackForm =
    ((isExpiring && dayCount != null && dayCount <= 1) || variant === "exhausted") &&
    currentUser &&
    !currentUser.hasSubmittedFeedback;

  const handleDialogClose = () => {
    onClose();
  };

  const actionButtons = showFeedbackForm ? (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => handleFeedbackSubmit(true)}
        disabled={isSubmitting}
        className="group relative inline-flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-blue-600 shadow-[0_8px_20px_-6px_rgba(91,33,182,0.5)] hover:from-violet-700 hover:to-blue-700 transition active:scale-[0.99] disabled:opacity-70"
      >
        {isSubmitting ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        ) : (
          <>
            <Crown className="h-4 w-4 text-amber-300 fill-amber-300" />
            Submit & Upgrade Plan
          </>
        )}
      </button>
      <button
        type="button"
        onClick={handleCloseAndDismiss}
        disabled={isSubmitting}
        className="flex h-11 w-full items-center justify-center rounded-xl text-sm font-semibold text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-70"
      >
        Remind me later
      </button>
    </div>
  ) : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDialogClose()}>
      <DialogContent
        className={[
          "flex flex-col gap-0 border-0 bg-transparent p-0 shadow-none",
          "top-3 left-1/2 w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] max-h-[calc(100dvh-1.5rem)]",
          showFeedbackForm ? "h-[calc(100dvh-1.5rem)]" : "h-auto",
          "translate-x-[-50%] translate-y-0 overflow-hidden",
          "sm:top-[50%] sm:h-auto sm:max-h-[min(90dvh,880px)] sm:translate-y-[-50%]",
          showFeedbackForm ? "sm:max-w-[760px]" : "sm:max-w-[448px]",
          "[&>button]:right-2.5 [&>button]:top-2.5 [&>button]:z-30 [&>button]:rounded-full",
          "[&>button]:flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center",
          "[&>button]:bg-black/35 [&>button]:p-0 [&>button]:text-white [&>button]:opacity-100",
          "[&>button]:ring-1 [&>button]:ring-white/40 [&>button]:backdrop-blur-sm",
          "[&>button]:hover:bg-black/50 [&>button]:hover:opacity-100",
          "[&>button]:focus:ring-white/50 [&>button]:focus:ring-offset-0",
          "[&>button>svg]:h-5 [&>button>svg]:w-5",
        ].join(" ")}
      >
        <div
          className="flex h-full max-h-[inherit] flex-col overflow-hidden rounded-[1.25rem] bg-white shadow-[0_24px_64px_-28px_rgba(76,29,149,0.45)] ring-1 ring-black/5"
          style={{ animation: "planModalPop 380ms cubic-bezier(0.22,1,0.36,1)" }}
        >
          {/* Hero */}
          <div
            className={`relative shrink-0 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5 ${
              isExpiring
                ? "bg-gradient-to-br from-[#6D28D9] via-[#5B21B6] to-[#2563EB]"
                : "bg-gradient-to-br from-[#F43F5E] via-[#F97316] to-[#F59E0B]"
            }`}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(ellipse 80% 70% at 100% 0%, rgba(255,255,255,0.28), transparent 55%), radial-gradient(ellipse 60% 50% at 0% 100%, rgba(255,255,255,0.12), transparent 50%)",
              }}
            />
            {/* subtle grid */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
                backgroundSize: "22px 22px",
                maskImage:
                  "linear-gradient(to bottom, black 0%, transparent 85%)",
              }}
            />

            <div className="relative pr-10">
              <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
                  VirtualScribe
                </span>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm ${
                    isExpiring
                      ? "bg-white text-violet-700"
                      : "bg-white text-orange-700"
                  }`}
                >
                  {statusChip}
                </span>
              </div>

              <div className="flex items-center gap-3 sm:gap-4">
                {isExpiring && dayCount != null ? (
                  <CountdownRing days={dayCount} warnDays={3} />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur-sm sm:h-[76px] sm:w-[76px]">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-white sm:text-sm">
                      Ended
                    </span>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <DialogTitle className="text-left text-xl font-semibold leading-[1.2] tracking-tight text-white sm:text-[1.5rem]">
                    {title}
                  </DialogTitle>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/90 sm:mt-2">
                    {description}
                  </p>
                </div>
              </div>

              {isExpiring && dayCount != null && (
                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-white/75">
                    <span>Trial window</span>
                    <span>
                      {dayCount === 0 ? "Final day" : `${dayCount} remaining`}
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/20">
                    <div
                      className="h-full rounded-full bg-white shadow-sm transition-all duration-700 ease-out"
                      style={{
                        width: `${Math.max(
                          8,
                          Math.round((dayCount / 3) * 100)
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-5">
            {showFeedbackForm ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
                
                {/* Left Column: Unlock features */}
                <div className="space-y-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                      What you unlock
                    </p>
                    <span className="text-xs font-medium text-violet-600">
                      Starter · Pro
                    </span>
                  </div>

                  <ul className="space-y-2">
                    {BENEFITS.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <li
                          key={item.title}
                          className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5 transition hover:border-violet-100 hover:bg-violet-50/50"
                          style={{
                            animation: `planModalRise 420ms ${80 + i * 55}ms both`,
                          }}
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-violet-600 shadow-sm ring-1 ring-slate-100">
                            <Icon className="h-4 w-4" strokeWidth={2} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900">
                              {item.title}
                            </p>
                            <p className="text-xs text-slate-500">{item.detail}</p>
                          </div>
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Right Column: Rating submission form */}
                <div className="space-y-4 border-t sm:border-t-0 sm:border-l border-slate-100 pt-5 sm:pt-0 sm:pl-6">
                  <div className="flex flex-col items-center gap-2 p-3 bg-slate-50 border border-slate-100 rounded-xl">
                    <span className="text-sm font-semibold text-slate-700">Overall Rating</span>
                    <div className="flex items-center gap-1.5">
                      {[1, 2, 3, 4, 5].map((star) => {
                        const isActive = hoverRating ? star <= hoverRating : rating !== null && star <= rating;
                        return (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            disabled={isSubmitting}
                            className="p-0.5 focus:outline-none transition-transform duration-100 hover:scale-110 active:scale-95 text-slate-300"
                          >
                            <Star
                              className={`h-7 w-7 transition-colors ${
                                isActive ? "fill-amber-400 text-amber-500" : "text-slate-300"
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                    {(hoverRating || rating) ? (
                      <span className={`text-sm font-semibold ${ratingDescriptions[hoverRating || rating].color}`}>
                        {ratingDescriptions[hoverRating || rating].emoji} {ratingDescriptions[hoverRating || rating].text}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Select a star rating</span>
                    )}
                  </div>

                  {/* Category select */}
                  <div className="space-y-1.5">
                    <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Category</span>
                    <div className="grid grid-cols-2 gap-1.5">
                      {categories.map((cat) => {
                        const isSelected = selectedCategory === cat.value;
                        return (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => setSelectedCategory(cat.value)}
                            disabled={isSubmitting}
                            className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border text-left transition ${
                              isSelected
                                ? "bg-violet-50 border-violet-200 text-violet-700"
                                : "bg-slate-50/50 border-slate-100 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                            }`}
                          >
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="space-y-1.5">
                    <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Comments</span>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us what you liked or what could be improved..."
                      rows={2}
                      disabled={isSubmitting}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-2.5 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  </div>

                  <div className="hidden sm:block pt-2 border-t border-slate-100">
                    {actionButtons}
                  </div>
                </div>

              </div>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                    What you unlock
                  </p>
                  <span className="text-xs font-medium text-violet-600">
                    Starter · Pro
                  </span>
                </div>

                <ul className="space-y-2">
                  {BENEFITS.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <li
                        key={item.title}
                        className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5 transition hover:border-violet-100 hover:bg-violet-50/50"
                        style={{
                          animation: `planModalRise 420ms ${80 + i * 55}ms both`,
                        }}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-violet-600 shadow-sm ring-1 ring-slate-100">
                          <Icon className="h-4 w-4" strokeWidth={2} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900">
                            {item.title}
                          </p>
                          <p className="text-xs text-slate-500">{item.detail}</p>
                        </div>
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-5 space-y-2">
                  <button
                    type="button"
                    onClick={() => onSubscribe?.()}
                    disabled={isLoading}
                    className={`group relative inline-flex h-[46px] w-full items-center justify-center gap-2 overflow-hidden rounded-xl text-[14px] font-semibold text-white transition active:scale-[0.99] disabled:opacity-70 ${
                      isExpiring
                        ? "bg-gradient-to-r from-violet-600 to-blue-600 shadow-[0_10px_24px_-8px_rgba(91,33,182,0.55)] hover:from-violet-700 hover:to-blue-700"
                        : "bg-gradient-to-r from-rose-500 to-orange-500 shadow-[0_10px_24px_-8px_rgba(244,63,94,0.45)] hover:from-rose-600 hover:to-orange-600"
                    }`}
                  >
                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition duration-700 group-hover:translate-x-full" />
                    {isLoading ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    ) : (
                      <>
                        {isExpiring ? "Upgrade & keep access" : "Choose a plan"}
                        <ArrowRight className="h-4 w-4 transition duration-200 group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={onViewPricing}
                    className="h-10 w-full rounded-xl text-sm font-medium text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    Compare plans
                  </button>

                  <button
                    type="button"
                    onClick={onClose}
                    className="flex h-11 w-full items-center justify-center rounded-xl text-sm font-semibold text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    Remind me later
                  </button>
                </div>

                <p className="mt-4 border-t border-slate-100 pt-3 text-center text-xs leading-relaxed text-slate-400">
                  Change or cancel anytime · HIPAA BAA included on every tier
                </p>
              </>
            )}
          </div>
          {showFeedbackForm && (
            <div className="shrink-0 border-t border-slate-100 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:hidden">
              {actionButtons}
            </div>
          )}
        </div>

        <style>{`
          @keyframes planModalPop {
            from { opacity: 0; transform: translateY(12px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes planModalRise {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default PlanReminderModal;
